import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ImagePlus, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, Loading, handleErr } from "./AdminUI";

const Area = ({ label, value, onChange, rows = 3, ...rest }) => (
  <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
    <textarea value={value || ""} onChange={onChange} rows={rows} {...rest}
      className="mt-1.5 w-full rounded-xl bg-[#0A0C10] border border-[#273042] px-4 py-2.5 text-white text-sm outline-none focus:border-[#E11D2A]/60" /></label>
);

async function uploadPhoto(file) { const fd = new FormData(); fd.append("file", file); const { data } = await endpoints.uploadImage(fd); return data.url; }

const ImageField = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (file) => { if (!file) return; setBusy(true); try { onChange(await uploadPhoto(file)); toast.success("Image uploaded"); } catch (e) { handleErr(e); } finally { setBusy(false); } };
  return (
    <div><span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
      <div className="mt-1.5 flex items-center gap-3">
        <label className="h-11 px-4 rounded-xl border border-[#273042] bg-[#0A0C10] text-slate-300 text-sm cursor-pointer flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        </label>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste image URL" className="flex-1 h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white text-sm outline-none" />
        {value && <img src={value} alt="" className="w-11 h-11 rounded-lg object-cover" />}
      </div>
    </div>
  );
};

export default function AdminHomepage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { endpoints.adminGetSettings().then((r) => setS(r.data)).catch(handleErr); }, []);
  if (!s) return <Loading />;
  const h = s.home || {};
  const setH = (k, v) => setS({ ...s, home: { ...h, [k]: v } });
  const setList = (k, v) => setS({ ...s, [k]: v.split(",").map((x) => x.trim()).filter(Boolean) });

  const save = async () => {
    setSaving(true);
    try { await endpoints.adminUpdateSettings({ stationName: s.stationName, slogan: s.slogan, tagline: s.tagline, stationTagline: s.tagline,
      newsCategories: s.newsCategories, newsLanguages: s.newsLanguages, home: s.home });
      toast.success("Homepage content saved"); } catch (e) { handleErr(e); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Homepage Content" desc="Edit the wording, slogans and images shown to customers. Changes go live instantly.">
        <Btn onClick={save} disabled={saving} data-testid="homepage-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}</Btn>
      </PageHeader>

      <div className="space-y-6">
        <Card className="p-5 space-y-3">
          <h3 className="text-white font-bold text-sm">Brand & tagline</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Station name" value={s.stationName || ""} onChange={(e) => setS({ ...s, stationName: e.target.value })} data-testid="hp-station" />
            <Field label="Slogan" value={s.slogan || ""} onChange={(e) => setS({ ...s, slogan: e.target.value, tagline: e.target.value })} />
            <Field label="Frequency" value={s.frequency || ""} onChange={(e) => setS({ ...s, frequency: e.target.value })} />
          </div>
        </Card>

        <Card className="p-5 space-y-3">
          <h3 className="text-white font-bold text-sm">Hero section</h3>
          <Field label="Badge text" value={h.heroBadge} onChange={(e) => setH("heroBadge", e.target.value)} />
          <Field label="Hero slogan" value={h.heroSlogan} onChange={(e) => setH("heroSlogan", e.target.value)} data-testid="hp-hero-slogan" />
          <Area label="Hero description" value={h.heroDescription} onChange={(e) => setH("heroDescription", e.target.value)} rows={3} />
          <ImageField label="Hero image" value={h.heroImage} onChange={(v) => setH("heroImage", v)} />
        </Card>

        <Card className="p-5 space-y-3">
          <h3 className="text-white font-bold text-sm">Who we are</h3>
          <Field label="Title" value={h.whoTitle} onChange={(e) => setH("whoTitle", e.target.value)} />
          <Area label="Text" value={h.whoText} onChange={(e) => setH("whoText", e.target.value)} rows={5} data-testid="hp-who-text" />
          <Field label="Image quote" value={h.whoQuote} onChange={(e) => setH("whoQuote", e.target.value)} />
          <ImageField label="Who-we-are image" value={h.whoImage} onChange={(v) => setH("whoImage", v)} />
        </Card>

        <Card className="p-5 space-y-3">
          <h3 className="text-white font-bold text-sm">Journalists & Premium</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Journalists title" value={h.journalistsTitle} onChange={(e) => setH("journalistsTitle", e.target.value)} />
            <Field label="Journalists subtitle" value={h.journalistsSubtitle} onChange={(e) => setH("journalistsSubtitle", e.target.value)} />
            <Field label="Premium label" value={h.premiumLabel} onChange={(e) => setH("premiumLabel", e.target.value)} />
            <Field label="Premium title" value={h.premiumTitle} onChange={(e) => setH("premiumTitle", e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-4">
            {[["showJournalists", "Show journalists"], ["showWho", "Show who-we-are"], ["showHow", "Show how-it-works"], ["showWhy", "Show why-we-exist"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={h[k] !== false} onChange={(e) => setH(k, e.target.checked)} /> {l}</label>
            ))}
          </div>
        </Card>

        <Card className="p-5 space-y-3">
          <h3 className="text-white font-bold text-sm">News categories & languages</h3>
          <Field label="Categories (comma-separated)" value={(s.newsCategories || []).join(", ")} onChange={(e) => setList("newsCategories", e.target.value)} data-testid="hp-news-cats" />
          <Field label="Languages (comma-separated)" value={(s.newsLanguages || []).join(", ")} onChange={(e) => setList("newsLanguages", e.target.value)} />
        </Card>
      </div>
    </div>
  );
}
